/**
 * Canister ID Registry - Sprint 31
 *
 * Provides canister IDs for different environments (local, mainnet, staging).
 * IDs are resolved at runtime based on the detected environment.
 *
 * Usage:
 * ```typescript
 * import { getCanisterId, CanisterName } from './config/canister-ids';
 *
 * const vectorDbId = getCanisterId('vector_db');
 * ```
 */

import { environment, EnvironmentMode } from './environment';

// ============================================================================
// Types
// ============================================================================

/**
 * Valid canister names in the ASD system
 */
export type CanisterName =
    | 'frontend'
    | 'discovery_engine'
    | 'vector_db'
    | 'credential_manager'
    | 'blockchain_storage'
    | 'ada'
    | 'aws_discovery_integration'
    | 'user_manager'
    | 'ii_agent'
    | 'service_mapper'
    | 'knowledge_base'
    | 'discovery_doctor'
    | 'claude_proxy'
    | 'llm_proxy';

/**
 * Canister ID registry for an environment
 */
export type CanisterIdMap = Record<CanisterName, string>;

/**
 * Full registry for all environments
 */
export interface CanisterIdRegistry {
    local: Partial<CanisterIdMap>;
    mainnet: Partial<CanisterIdMap>;
    staging: Partial<CanisterIdMap>;
}

// ============================================================================
// Canister IDs
// ============================================================================

/**
 * Local development canister IDs
 * These are generated by dfx and stored in .dfx/local/canister_ids.json
 * Updated: 2026-02-03 from `dfx canister id <name>`
 *
 * IMPORTANT: These IDs change when you run `dfx start --clean`.
 * After a clean start, run `dfx deploy` then update these IDs, or
 * use `dfx generate` to regenerate declarations (which AgentAdapter imports).
 */
const LOCAL_CANISTER_IDS: Partial<CanisterIdMap> = {
    frontend: 'vizcg-th777-77774-qaaea-cai',
    discovery_engine: 'ufxgi-4p777-77774-qaadq-cai',
    vector_db: 'v27v7-7x777-77774-qaaha-cai',
    credential_manager: 'ulvla-h7777-77774-qaacq-cai',
    blockchain_storage: 'uzt4z-lp777-77774-qaabq-cai',
    ada: 'uxrrr-q7777-77774-qaaaq-cai',
    aws_discovery_integration: 'u6s2n-gx777-77774-qaaba-cai',
    user_manager: 'vt46d-j7777-77774-qaagq-cai',
    ii_agent: 'vpyes-67777-77774-qaaeq-cai',
    service_mapper: 'vu5yx-eh777-77774-qaaga-cai',
    knowledge_base: 'vg3po-ix777-77774-qaafa-cai',
    discovery_doctor: 'ucwa4-rx777-77774-qaada-cai',
    claude_proxy: 'umunu-kh777-77774-qaaca-cai',
    llm_proxy: 'vb2j2-fp777-77774-qaafq-cai'
};

/**
 * Mainnet canister IDs
 * Deployed: 2026-01-21
 * Format: <canister-id>-cai
 */
const MAINNET_CANISTER_IDS: Partial<CanisterIdMap> = {
    frontend: 'd73iu-5qaaa-aaaae-qgxoa-cai',
    discovery_engine: 'dk4zz-4yaaa-aaaae-qgxnq-cai',
    vector_db: 'gebuf-3aaaa-aaaae-qgxra-cai',
    credential_manager: 'de6ur-hiaaa-aaaae-qgxmq-cai',
    blockchain_storage: 'corwl-fyaaa-aaaae-qgxlq-cai',
    ada: 'cat3d-6iaaa-aaaae-qgxkq-cai',
    aws_discovery_integration: 'cjqq7-iaaaa-aaaae-qgxla-cai',
    user_manager: 'gnc7z-niaaa-aaaae-qgxqq-cai',
    ii_agent: 'dy2oa-qiaaa-aaaae-qgxoq-cai',
    service_mapper: 'gkdzn-aqaaa-aaaae-qgxqa-cai',
    knowledge_base: 'drzf4-gaaaa-aaaae-qgxpa-cai',
    discovery_doctor: 'dn57n-raaaa-aaaae-qgxna-cai',
    claude_proxy: 'dd7sf-kqaaa-aaaae-qgxma-cai',
    llm_proxy: 'dwydi-lyaaa-aaaae-qgxpq-cai'
};

/**
 * Staging canister IDs (if applicable)
 */
const STAGING_CANISTER_IDS: Partial<CanisterIdMap> = {
    // TODO: Populate if staging environment is set up
};

/**
 * Full registry
 */
const CANISTER_REGISTRY: CanisterIdRegistry = {
    local: LOCAL_CANISTER_IDS,
    mainnet: MAINNET_CANISTER_IDS,
    staging: STAGING_CANISTER_IDS
};

// ============================================================================
// Lookup Functions
// ============================================================================

/**
 * Get canister ID for the current environment
 * @param canisterName - Name of the canister
 * @returns Canister ID string
 * @throws Error if canister ID not found
 */
export function getCanisterId(canisterName: CanisterName): string {
    const envIds = CANISTER_REGISTRY[environment.mode];
    const canisterId = envIds[canisterName];

    if (!canisterId) {
        // Fallback to local IDs if mainnet IDs not configured
        if (environment.mode !== 'local' && LOCAL_CANISTER_IDS[canisterName]) {
            console.warn(
                `⚠️ Canister ID for "${canisterName}" not configured for ${environment.mode}. ` +
                `Falling back to local ID for testing.`
            );
            return LOCAL_CANISTER_IDS[canisterName]!;
        }

        throw new Error(
            `Canister ID not found for "${canisterName}" in ${environment.mode} environment. ` +
            `Please configure MAINNET_CANISTER_IDS in canister-ids.ts after deployment.`
        );
    }

    return canisterId;
}

/**
 * Get canister ID with optional fallback to local environment
 * @param canisterName - Name of the canister
 * @param fallback - Optional fallback ID if not found (defaults to local ID)
 * @returns Canister ID string or fallback
 */
export function getCanisterIdOrDefault(canisterName: CanisterName, fallback?: string): string {
    try {
        return getCanisterId(canisterName);
    } catch {
        // Use provided fallback, or fall back to local ID
        return fallback || LOCAL_CANISTER_IDS[canisterName] || '';
    }
}

/**
 * Check if a canister ID is configured for current environment
 * @param canisterName - Name of the canister
 */
export function hasCanisterId(canisterName: CanisterName): boolean {
    const envIds = CANISTER_REGISTRY[environment.mode];
    return !!envIds[canisterName];
}

/**
 * Get all canister IDs for current environment
 */
export function getAllCanisterIds(): Partial<CanisterIdMap> {
    return { ...CANISTER_REGISTRY[environment.mode] };
}

/**
 * Get canister IDs for a specific environment
 * @param mode - Environment mode
 */
export function getCanisterIdsForEnvironment(mode: EnvironmentMode): Partial<CanisterIdMap> {
    return { ...CANISTER_REGISTRY[mode] };
}

// ============================================================================
// Update Functions (for dynamic ID management)
// ============================================================================

/**
 * Update a canister ID at runtime
 * Useful for loading IDs from a configuration endpoint
 * @param mode - Environment mode
 * @param canisterName - Canister name
 * @param canisterId - New canister ID
 */
export function updateCanisterId(
    mode: EnvironmentMode,
    canisterName: CanisterName,
    canisterId: string
): void {
    CANISTER_REGISTRY[mode][canisterName] = canisterId;
    console.log(`📝 Updated ${canisterName} canister ID for ${mode}: ${canisterId}`);
}

/**
 * Bulk update canister IDs
 * @param mode - Environment mode
 * @param ids - Map of canister names to IDs
 */
export function updateCanisterIds(
    mode: EnvironmentMode,
    ids: Partial<CanisterIdMap>
): void {
    Object.entries(ids).forEach(([name, id]) => {
        if (id) {
            CANISTER_REGISTRY[mode][name as CanisterName] = id;
        }
    });
    console.log(`📝 Updated ${Object.keys(ids).length} canister IDs for ${mode}`);
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate all required canisters are configured
 * @param requiredCanisters - List of required canister names
 * @returns Validation result
 */
export function validateCanisterIds(
    requiredCanisters: CanisterName[]
): { valid: boolean; missing: CanisterName[] } {
    const missing = requiredCanisters.filter(name => !hasCanisterId(name));
    return {
        valid: missing.length === 0,
        missing
    };
}

// ============================================================================
// Initialization
// ============================================================================

// Log canister ID status on load
if (typeof window !== 'undefined' && environment.debug) {
    const ids = getAllCanisterIds();
    const count = Object.keys(ids).length;
    console.log(`📋 Canister IDs loaded for ${environment.mode}: ${count} canisters`);

    if (environment.mode !== 'local' && count === 0) {
        console.warn(
            '⚠️ No canister IDs configured for mainnet. ' +
            'Please update MAINNET_CANISTER_IDS in canister-ids.ts after deployment.'
        );
    }
}

// Expose on window for debugging
if (typeof window !== 'undefined') {
    (window as any).asdCanisterIds = {
        get: getCanisterId,
        getAll: getAllCanisterIds,
        has: hasCanisterId,
        validate: validateCanisterIds,
        update: updateCanisterId
    };
}
